import React, { useEffect, useState, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { client, urlFor } from "../sanity/sanityClient";
import styled from "styled-components";
import apiClient from "../utils/apiClient";
import CryptoJS from "crypto-js";
import { v4 as uuidv4 } from "uuid";
import { GoArrowLeft } from "react-icons/go";
import { MdDeleteForever, MdStorefront } from "react-icons/md";
import ChatInput from "./ChatInput";
import Logout from "./Logout";
import Scraper from "./Scraper";
import SurpriseModal from "./SurpriseModal";
import { onlineUsersRoute } from "../utils/APIRoutes";
import { getCachedMessages, cacheMessages, addLocalMessage, 
 updateLocalMessage,
  replaceLocalMessage, getPendingMessages } from '../db';
import useOnlineStatus from '../useOnlineStatus';

import messageSound from "../assets/sounds/message.mp3";
import {
  sendmessageroute,
  recievemessageroute,
  deletemessageroute,
  deleteonemessageroute,
} from "../utils/APIRoutes";

const FloatingDateBadge = ({ date }) => {
  return (
    <div
      style={{
        position: "sticky",
        top: "10px",
        zIndex: 1,
        backgroundColor: "#f0f0f0",
        padding: "5px 10px",
        borderRadius: "10px",
        margin: "10px auto",
        textAlign: "center",
        width: "fit-content",
        boxShadow: "0 2px 5px rgba(0, 0, 0, 0.1)",
      }}
    >
      {date}
    </div>
  );
}

export default function ChatContainer({
  currentChat,
  currentUser,
  socket,
  updateConversation,
  fetchTraderProducts,

  /* Scraper */
  categories,
  isLoading,
  isTrader,
  traderProducts,
  onSelectedProduct,

  /* Product */
  selectedProduct,
  productDeselect,

  handleBack,
}) {
  const [messages, setMessages] = useState([]);
  const [arrivalMessage, setArrivalMessage] = useState(null);
  const [typingUser, setTypingUser] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [hoveredMessageId, setHoveredMessageId] = useState(null);
  const longPressTimer = useRef(null);
  const [newCategories, setNewCategories] = useState([]);
  const scrollRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const [isOnline, setIsOnline] = useState(false);

  // ✅ READ RECEIPT: ref to track currentChat inside socket listeners
  const currentChatRef = useRef(currentChat);
  useEffect(() => {
    currentChatRef.current = currentChat;
  }, [currentChat]);

  // ============ RECONCILE ON RECONNECT — flush first, merge after ============
useOnlineStatus(async () => {
  if (!currentChat) return;
  const user = JSON.parse(localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY));

  // 1. Actively resend anything still pending for THIS chat. This is
  //    the fix — no longer waiting on/trusting the native Background
  //    Sync API to have already done this.
  await flushPendingMessages(currentChat._id);

  // 2. THEN fetch the authoritative history and merge (not replace).
  //
  // SECURITY: swapped `axios` for `apiClient` here (and at every other
  // call site in this file) so these requests actually carry the
  // session cookie the backend auth middleware expects — plain axios
  // calls would otherwise 401. Route name/behavior unchanged.
  try {
    const { data } = await apiClient.post(recievemessageroute, { from: user._id, to: currentChat._id });
    const parsed = data.messages.map((m) => ({ ...m, message: decrypt(m.message), status: 'sent' }));

    setMessages((prev) => {
      const stillPending = prev.filter((m) => m.status === 'pending' || m.status === 'sending');
      const parsedIds = new Set(parsed.map((m) => m._id));
      const trulyPending = stillPending.filter((m) => !parsedIds.has(m._id));
      return [...parsed, ...trulyPending].sort(
        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
      );
    });

    await cacheMessages(currentChat._id, parsed);
  } catch (err) {
    console.error('Reconcile-on-reconnect failed:', err.message);
  }
});

  useEffect(() => {
  if (!currentChat) return;

  const fetchOnlineStatus = async () => {
    try {
      const { data } = await apiClient.get(onlineUsersRoute);

      setIsOnline(
        data.some((user) => user.userId === currentChat._id)
      );
    } catch (err) {
      console.error(err);
    }
  };

  fetchOnlineStatus();
}, [currentChat]);


  /* ================= ENCRYPTION ================= */
  const encrypt = (msg) =>
    CryptoJS.AES.encrypt(msg, process.env.REACT_APP_SECRET_KEY).toString();

  const decrypt = (cipher) => {
    const bytes = CryptoJS.AES.decrypt(
      cipher,
      process.env.REACT_APP_SECRET_KEY
    );
    return bytes.toString(CryptoJS.enc.Utf8);
  };

  // ============ NEW: actively resend anything still pending ============
// Called on reconnect AND whenever a chat is opened (covers reopening
// the app after being offline, not just the live online/offline
// transition). clientMessageId reuses the message's own tempId — if
// Background Sync (where supported) ALSO independently delivered the
// same message in the meantime, the server's unique index on
// clientMessageId means our controller returns the existing message
// instead of creating a duplicate (see addMessage.js), so this is
// always safe to call even if a native replay already ran in parallel.
const flushPendingMessages = useCallback(async (chatId) => {
  if (!currentChat) return;
  const user = JSON.parse(localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY));
  const pending = await getPendingMessages(chatId);

  for (const pendingMsg of pending) {
    try {
      const { data } = await apiClient.post(sendmessageroute, {
        from: user._id,
        to: currentChat._id,
        message: encrypt(pendingMsg.message),
        clientMessageId: pendingMsg._id,
      });

      const realMessage = { ...data.message, message: pendingMsg.message };
      await replaceLocalMessage(pendingMsg._id, realMessage, chatId);

      socket.current?.emit("send-msg", {
        from: user._id, to: currentChat._id, msg: encrypt(pendingMsg.message),
        sender: user.username, receiver: currentChat.email,
      });
    } catch (err) {
      console.error(`Resend failed for pending message ${pendingMsg._id} (will retry next reconnect):`, err.message);
      // Deliberately NOT deleted, NOT marked failed — stays 'pending'
      // so the next reconnect (or next flush) tries again. This is
      // the key difference from the old clearStaleLocalMessages
      // behavior: a still-unresolved message is never destroyed.
    }
  }
}, [currentChat, socket]);

// ============ FETCH MESSAGES — now flushes pending first, merges after ============
useEffect(() => {
  if (!currentChat) return;
  const user = JSON.parse(localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY));

  (async () => {
    const cached = await getCachedMessages(currentChat._id);
    if (cached.length) setMessages(cached);

    // Opening a chat is also a reasonable moment to attempt delivery
    // of anything still stuck pending from a previous offline session
    // — not just the live reconnect event.
    if (navigator.onLine) {
      await flushPendingMessages(currentChat._id);
    }

    try {
      const { data } = await apiClient.post(recievemessageroute, { from: user._id, to: currentChat._id });
      const parsed = data.messages.map((m) => ({ ...m, message: decrypt(m.message), status: 'sent' }));

      // MERGE, never blind-replace: preserve any message that's still
      // genuinely pending (the flush above may have just failed again)
      // so it keeps showing with its clock icon instead of vanishing.
      setMessages((prev) => {
        const stillPending = prev.filter((m) => m.status === 'pending' || m.status === 'sending');
        const parsedIds = new Set(parsed.map((m) => m._id));
        const trulyPending = stillPending.filter((m) => !parsedIds.has(m._id));
        return [...parsed, ...trulyPending].sort(
          (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
        );
      });

      await cacheMessages(currentChat._id, parsed);
      // Note: clearStaleLocalMessages is intentionally NOT called here
      // anymore. replaceLocalMessage already removes a temp row the
      // moment its resend succeeds — that's the only safe place to
      // delete one. A blanket post-fetch sweep can't tell "confirmed
      // gone" apart from "still legitimately in flight."

      if (parsed.length > 0 && socket.current) {
        socket.current.emit("read-receipt", { from: user._id, to: currentChat._id });
      }
    } catch (err) {
      console.error('Message fetch failed (likely offline):', err.message);
    }
  })();
}, [currentChat, socket, flushPendingMessages]);


  // Group messages by date
  const groupMessagesByDate = (messages) => {
    const groupedMessages = [];
    let currentDate = null;

    messages.forEach((message) => {
      const messageDate = message.date || new Date().toLocaleDateString();
      if (messageDate !== currentDate) {
        groupedMessages.push({ type: "date", date: messageDate });
        currentDate = messageDate;
      }
      groupedMessages.push({ type: "message", ...message });
    });

    return groupedMessages;
  };

  /* ================= SEND MESSAGE ================= */
const handleSendMsg = useCallback(async (msg) => {
  const user = JSON.parse(localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY));
  const encrypted = encrypt(msg);

  // A temp id we own up front, since _id is now the Dexie primary key
  // and addLocalMessage requires the caller to supply one. Prefixed
  // so clearStaleLocalMessages can recognize and clean up orphans.
  const tempId = `temp-${uuidv4()}`;

  const localMessage = {
    _id: tempId,
    fromSelf: true,
    message: msg,
    date: new Date().toLocaleDateString(),
    stamp: new Date().toLocaleTimeString(),
    timestamp: new Date().toISOString(),
    readBy: [user._id],
    status: navigator.onLine ? 'sending' : 'pending',
    chatId: currentChat._id,
  };

  await addLocalMessage(localMessage);
  setMessages((prev) => [...prev, localMessage]);

  socket.current?.emit("send-msg", {
    from: user._id, to: currentChat._id, msg: encrypted,
    sender: user.username, receiver: currentChat.email,
  });

  // SECURITY: `from: user._id` here (and in every other call in this
  // file, plus the socket "send-msg"/"typing"/"read-receipt" emits
  // below) is a client-declared value from localStorage — the message
  // routes and socket handlers behind these calls need to verify it
  // against the authenticated session (req.userId on HTTP calls,
  // socket.userId after wiring middleware/socketAuth.js) rather than
  // trusting it outright, the same IDOR pattern already fixed on
  // userRoutes.js. Not changed here since it's backend work and out of
  // scope for this pass — flagging at the call site since this is
  // where "from" gets set.
  try {
    const { data } = await apiClient.post(sendmessageroute, {
      from: user._id,
      to: currentChat._id,
      message: encrypted,
      clientMessageId: tempId, // reuse the same id server-side for dedupe (see addMessage.js)
    });

    const realMessage = {
      ...data.message,
      message: msg, // already-decrypted plaintext, no need to decrypt our own echo
    };

    // Atomically swap the temp row for the real one in IndexedDB...
    await replaceLocalMessage(tempId, realMessage, currentChat._id);

    // ...and in React state: replace the temp entry with the real one
    // rather than just flipping its status, so its _id (and therefore
    // its render key) becomes the real server id going forward. This
    // is what prevents a later full-history fetch from ever having
    // both the temp and the real copy of the same message on screen
    // at once.
    setMessages((prev) =>
      prev.map((m) => (m._id === tempId ? { ...realMessage, status: 'sent' } : m))
    );

    updateConversation(currentChat);
  } catch (err) {
    console.error('Send failed (likely offline) — Background Sync will retry:', err.message);
    await updateLocalMessage(tempId, { status: 'pending' });
    setMessages((prev) => prev.map((m) => (m._id === tempId ? { ...m, status: 'pending' } : m)));
  }
}, [currentChat, socket, updateConversation]);

  /* ================= SEND PRODUCT ================= */
  useEffect(() => {
    if (selectedProduct) {
      handleSendMsg(selectedProduct);
      productDeselect();
    }
  }, [selectedProduct, handleSendMsg, productDeselect]);

  /* ================= SOCKET LISTENERS ================= */
  useEffect(() => {
    const socketInstance = socket.current;
    if (!socketInstance) return;

    socketInstance.on("msg-recieve", (data) => {
      const msg = decrypt(data.msg);

      setArrivalMessage({
        fromSelf: false,
        message: msg,
        date: new Date().toLocaleDateString(),
        stamp: new Date().toLocaleTimeString(),
      });
      new Audio(messageSound).play();
      updateConversation(
          {
              ...currentChatRef.current,
              _id: data.from,
              unreadCount:
                  currentChatRef.current?._id === data.from
                      ? 0
                      : (currentChatRef.current?.unreadCount || 0) + 1
          },
          "receive"
          );

      // ✅ READ RECEIPT: auto-mark as read if this chat is open
      if (currentChatRef.current && socketInstance) {
        const user = JSON.parse(
          localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY)
        );
        socketInstance.emit("read-receipt", {
          from: user._id,
          to: currentChatRef.current._id,
        });
      }
    });

    socketInstance.on("user-typing", ({ from, isTyping }) => {
      setTypingUser(isTyping ? from : null);
    });

    // ✅ READ RECEIPT: update messages when the other user reads them
    socketInstance.on("read-receipt", ({ from, to }) => {
      setMessages((prev) =>
        prev.map((msg) => {
          if (msg.fromSelf && msg.readBy && !msg.readBy.includes(from)) {
            return { ...msg, readBy: [...msg.readBy, from] };
          }
          return msg;
        })
      );
    });

    socketInstance.on("user-status-change", ({ userId, isOnline }) => {
        if (currentChatRef.current?._id === userId) {
          setIsOnline(isOnline);
        }
      });

    return () => {
      socketInstance.off("msg-recieve");
      socketInstance.off("user-typing");
      socketInstance.off("read-receipt");
      socketInstance.off("user-status-change");
    };
  }, [socket, updateConversation]); // ✅ socket is in deps, but we used local variable

  useEffect(() => {
    arrivalMessage && setMessages((prev) => [...prev, arrivalMessage]);
  }, [arrivalMessage]);

  useEffect(() => {
    scrollRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  /* ================= TYPING ================= */
  const handleKeyDown = () => {
    const user = JSON.parse(
      localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY)
    );

    socket.current.emit("typing", {
      from: user._id,
      to: currentChat._id,
    });
  };

  const handleKeyUp = () => {
    const user = JSON.parse(
      localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY)
    );

    clearTimeout(typingTimeoutRef.current);
    typingTimeoutRef.current = setTimeout(() => {
      socket.current.emit("stop-typing", {
        from: user._id,
        to: currentChat._id,
      });
    }, 800);
  };

  /* ================= DELETE ================= */
  const confirmDelete = async () => {
    const user = JSON.parse(
      localStorage.getItem(process.env.REACT_APP_LOCALHOST_KEY)
    );

    await apiClient.post(deletemessageroute, {
      from: user._id,
      to: currentChat._id,
    });

    setMessages([]);
    updateConversation(currentChat, "remove");
    setShowDeleteConfirm(false);
  };

  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const query = `
          *[_type in [ "clothingandfashion", "electronics", "arts", "fashion", "salon","truck2cargo", "grocery", "beauty", "crops", "energyandfuel", "cuisines", "booksandstationery",
         "sports", "kitchen", "hotels", "homeandfurniture", "hardware", "books", "estate", "foodandbeverages", "agritech", "giftoptions", "servicesandinstitutions",
          "homeandfurnitures","livestock", "vehicles", "jewelryandaccessories", "medical", "useditems", "construction", "kidsandtoys", "eventsandnightclubs", "hospitality"]] {
            _id,
            _type,
            slug,
            image
          }
        `;
        const result = await client.fetch(query);
        setNewCategories(result);
      } catch (error) {
        console.error("Error fetching products:", error);
      }
    };

    fetchProducts();
  }, []);

  const deleteSingleMessage = async (messageId) => {
    try {
      await apiClient.post(deleteonemessageroute, {
        messageId,
      });

      // Optimistic UI update
      setMessages(prev => {
    const remaining = prev.filter(
        m => m._id !== messageId
    );

    // Conversation became empty
    if (remaining.length === 0) {
        updateConversation(currentChat, "remove");
        return remaining;
    }

    // Was the deleted message the latest one?
    const deletedMessage = prev.find(
        m => m._id === messageId
    );

    const latestRemaining =
        remaining[remaining.length - 1];

    const deletedWasLatest =
        deletedMessage &&
        deletedMessage._id ===
            prev[prev.length - 1]._id;

    if (deletedWasLatest) {
        updateConversation({
            ...currentChat,
            lastMessageTime:
                latestRemaining.createdAt ||
                latestRemaining.timestamp ||
                new Date().toISOString()
        });
    }

    return remaining;
});
    } catch (error) {
      console.error("Delete message error:", error);
    }
  };

  const handleTouchStart = (id) => {
    longPressTimer.current = setTimeout(() => {
      setHoveredMessageId(id);
    }, 500);
  };

  const handleTouchEnd = () => {
    clearTimeout(longPressTimer.current);
  };

  const dedupedMessages = React.useMemo(() => {
  const byId = new Map();
  for (const m of messages) {
    if (m._id) byId.set(m._id, m);
    else byId.set(Symbol(), m); // no _id (e.g. a bare arrivalMessage) — never collides
  }
  return Array.from(byId.values());
}, [messages]);

  /* ================= RENDER ================= */
  return (
    <Container>
      {/* ---------- TOP 90vh ---------- */}
      <div className="top">
        {/* LEFT COLUMN */}
        <div className="left">
          {/* HEADER */}
          <div className="header">
            <div
              className="user"
              onClick={() => fetchTraderProducts(currentChat._id)}
            >
              {currentChat.avatarImage ? (
                <>
                  {currentChat.avatarImage.startsWith("https://") ||
                  currentChat.avatarImage.includes("https") ? (
                    <img
                      src={currentChat.avatarImage}
                      alt={`${currentChat.username}'s avatar`}
                    />
                  ) : (
                    <img
                      src={`data:image/svg+xml;base64,${currentChat.avatarImage}`}
                      alt={`${currentChat.username}'s avatar`}
                    />
                  )}
                </>
              ) : (
                <img
                  src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAQAAAAEACAIAAADTED8xAAADMElEQVR4nOzVwQnAIBQFQYXff81RUkQCOyDj1YOPnbXWPmeTRef+/3O/OyBjzh3CD95BfqICMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMK0CMO0TAAD//2Anhf4QtqobAAAAAElFTkSuQmCC"
                  alt="Default Avatar"
                />
              )}
              <div>
  <h3>{currentChat.username}</h3>

  {typingUser === currentChat._id ? (
        <p
          style={{
            position: "absolute",
            margin: 0,
            fontSize: "8px",
            color: "#1aa31a",
            fontWeight: 600,
          }}
        >
          Typing...
        </p>
      ) : isOnline ? (
        <p
          style={{
            position: "absolute",
            margin: 0,
            fontSize: "8px",
            color: "#1aa31a",
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
            gap: "4px",
          }}
        >
          <span
            style={{
              width: "6px",
              height: "6px",
              borderRadius: "50%",
              background: "#1aa31a",
              display: "inline-block",
            }}
          />
          Online
        </p>
      ) : (
        currentChat.lastActive && (
          <p
            style={{
              position: "absolute",
              margin: 0,
              fontSize: "8px",
              color: "#777",
            }}
          >
            Last active{" "}
            {new Date(currentChat.lastActive).toLocaleString([], {
              year: "numeric",
              month: "numeric",
              day: "numeric",
              hour: "numeric",
              minute: "2-digit",
            })}
          </p>
        )
      )}
    </div>
    </div>
    <Logout/>
</div>

         {/* MESSAGES */}
<div className="messages">
  {dedupedMessages.length > 0 &&
    groupMessagesByDate(messages).map((item, index) => {

      /* ---------------------------------
         DATE BADGE
      --------------------------------- */
      if (item.type === "date") {
        return (
          <FloatingDateBadge
            key={`date-${index}`}
            date={item.date}
          />
        );
      }

      /* ---------------------------------
         NORMALIZE OLD + NEW MESSAGE FORMAT
      --------------------------------- */
      const message = {
        _id: item._id,

        fromSelf: item.fromSelf,

        message:
          typeof item.message === "string"
            ? item.message
            : item.message?.text || "",

        timestamp:
          item.timestamp ||
          item.createdAt ||
          "",

        stamp:
          item.stamp ||
          (item.createdAt
            ? new Date(item.createdAt).toLocaleTimeString(
                "default",
                {
                  hour: "numeric",
                  minute: "numeric",
                }
              )
            : ""),

        readBy: Array.isArray(item.readBy)
          ? item.readBy
          : [],

        replyTo: item.replyTo || null,

        // Drives the pending/sending/sent indicator below. Absent on
        // messages that came back from the server (they're always
        // 'sent' by definition), present on locally-queued ones that
        // haven't been confirmed yet — see handleSendMsg.
        status: item.status,
      };

      const timestamp = message.timestamp || "";

      /* ---------------------------------
         READ RECEIPT NORMALIZATION
         Older messages have no readBy
      --------------------------------- */
      const hasReadReceipt =
        message.fromSelf &&
        Array.isArray(message.readBy);

      const isRead =
        hasReadReceipt &&
        message.readBy.includes(
          currentChat._id
        );

      /* ---------------------------------
         IMAGE / PRODUCT MESSAGE
      --------------------------------- */
      if (
        typeof message.message === "string" &&
        message.message.includes("image-")
      ) {

        const matchedCategory =
          newCategories?.find(
            (category) =>
              category.image?.[0]?.asset?._ref ===
              message.message
          );

        return (
          <div
            ref={scrollRef}
            key={message._id || uuidv4()}
          >
            <div
              className={`bubble ${
                message.fromSelf
                  ? "sent"
                  : "received"
              }`}
              onMouseEnter={() =>
                setHoveredMessageId(
                  message._id
                )
              }
              onMouseLeave={() =>
                setHoveredMessageId(null)
              }
              onTouchStart={() =>
                handleTouchStart(
                  message._id
                )
              }
              onTouchEnd={handleTouchEnd}
            >
              <div className="content">

                {/* REPLY PREVIEW */}
                {message.replyTo && (
                  <div className="reply-preview">
                    <small>
                      {message.replyTo.sender ===
                      currentUser._id
                        ? "You"
                        : currentChat.username}
                    </small>

                    <p>
                      {message.replyTo.message}
                    </p>
                  </div>
                )}

                {matchedCategory && (
                  <Link
                    to={`/category/${matchedCategory.slug.current}`}
                  >
                    <img
                      className="option"
                      src={urlFor(
                        message.message
                      )}
                      alt="product"
                      width={120}
                    />
                  </Link>
                )}

                <span className="timestamp">
                  {timestamp
                    ? new Date(
                        timestamp
                      ).toLocaleTimeString(
                        "default",
                        {
                          hour: "numeric",
                          minute: "numeric",
                        }
                      )
                    : message.stamp}

                  {/* SEND STATUS / READ RECEIPTS */}
                  {message.fromSelf && (
                    <span
                      style={{
                        marginLeft: "4px",
                        fontSize: "12px",
                      }}
                    >
                      {message.status === 'pending' ? (
                        <span
                          style={{ color: '#999' }}
                          title="Will send when back online"
                        >
                          🕓
                        </span>
                      ) : message.status === 'sending' ? (
                        <span style={{ color: '#999' }}>
                          …
                        </span>
                      ) : !hasReadReceipt ? (
                        <span
                          style={{
                            color: "#32cd32",
                          }}
                        >
                          ✓
                        </span>
                      ) : isRead ? (
                        <span
                          style={{
                            color: "#1e90ff",
                          }}
                        >
                          ✓✓
                        </span>
                      ) : (
                        <span
                          style={{
                            color: "#32cd32",
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </span>
                  )}
                </span>

                {hoveredMessageId ===
                  message._id && (
                  <MdDeleteForever
                    style={{
                      position: "absolute",
                      background: "white",
                      right: "-4px",
                      top: "50%",
                      transform:
                        "translateY(-50%)",
                      cursor: "pointer",
                    }}
                    onClick={() =>
                      deleteSingleMessage(
                        message._id
                      )
                    }
                  />
                )}
              </div>
            </div>
          </div>
        );
      }

      /* ---------------------------------
         NORMAL TEXT MESSAGE
      --------------------------------- */
      return (
        <div
          ref={scrollRef}
          key={message._id || uuidv4()}
        >
          <div
            className={`bubble ${
              message.fromSelf
                ? "sent"
                : "received"
            }`}
            onMouseEnter={() =>
              setHoveredMessageId(
                message._id
              )
            }
            onMouseLeave={() =>
              setHoveredMessageId(null)
            }
            onTouchStart={() =>
              handleTouchStart(
                message._id
              )
            }
            onTouchEnd={handleTouchEnd}
          >
            <div className="content">

              {/* REPLY PREVIEW */}
              {message.replyTo && (
                <div className="reply-preview">
                  <small>
                    {message.replyTo.sender ===
                    currentUser._id
                      ? "You"
                      : currentChat.username}
                  </small>

                  <p>
                    {message.replyTo.message}
                  </p>
                </div>
              )}

              <p style={{ margin: 0 }}>
                {message.message}
              </p>

              <span className="timestamp">
                {timestamp
                  ? new Date(
                      timestamp
                    ).toLocaleTimeString(
                      "default",
                      {
                        hour: "numeric",
                        minute: "numeric",
                      }
                    )
                  : message.stamp}

                {/* SEND STATUS / READ RECEIPTS */}
                {message.fromSelf && (
                  <span
                    style={{
                      marginLeft: "4px",
                      fontSize: "12px",
                    }}
                  >
                    {message.status === 'pending' ? (
                      <span
                        style={{ color: '#999' }}
                        title="Will send when back online"
                      >
                        🕓
                      </span>
                    ) : message.status === 'sending' ? (
                      <span style={{ color: '#999' }}>
                        …
                      </span>
                    ) : !hasReadReceipt ? (
                      <span
                        style={{
                          color: "#32cd32",
                        }}
                      >
                        ✓
                      </span>
                    ) : isRead ? (
                      <span
                        style={{
                          color: "#1e90ff",
                        }}
                      >
                        ✓✓
                      </span>
                    ) : (
                      <span
                        style={{
                          color: "#32cd32",
                        }}
                      >
                        ✓
                      </span>
                    )}
                  </span>
                )}
              </span>

              {hoveredMessageId ===
                message._id && (
                <MdDeleteForever
                  style={{
                    position: "absolute",
                    background: "white",
                    right: "-4px",
                    top: "50%",
                    transform:
                      "translateY(-50%)",
                    cursor: "pointer",
                  }}
                  onClick={() =>
                    deleteSingleMessage(
                      message._id
                    )
                  }
                />
              )}
            </div>
          </div>
        </div>
      );
    })}
</div>

<div className="actions">
  <GoArrowLeft
    size={21}
    style={{
      marginLeft: "6px",
      cursor: "pointer",
      flexShrink: 0,
    }}
    onClick={handleBack}
  />

  {/* STORE LINK */}
  <Link
    to={`/shop/${currentChat._id}`}
    style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textDecoration: "none",
      color: "#111",
      flexShrink: 0,
    }}
  >
    <MdStorefront size={21} />
  </Link>

  {/* GIFT BUTTON */}
  <button
    style={{
      width: "36px",
      height: "36px",
      borderRadius: "2rem",
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      backgroundColor: "#ffffff34",
      border: "none",
      cursor: "pointer",
      padding: 0,
      flexShrink: 0,
      fontSize: "20px",
    }}
    onClick={() => setIsModalOpen(true)}
  >
    🎁
  </button>

  <MdDeleteForever
    size={21}
    style={{
      cursor: "pointer",
      flexShrink: 0,
    }}
    onClick={() => setShowDeleteConfirm(true)}
  />
</div>
        </div>

        {/* RIGHT COLUMN */}
        <div className="right">
          <Scraper
            categories={categories}
            isLoading={isLoading}
            isTrader={isTrader}
            traderProducts={traderProducts}
            onSelectedProduct={onSelectedProduct}
          />
        </div>
      </div>

      {/* ---------- FULL WIDTH INPUT ---------- */}
      <div className="input">
        <ChatInput
          handleSendMsg={handleSendMsg}
          handleKeyDown={handleKeyDown}
          handleKeyUp={handleKeyUp}
          selectedProduct={selectedProduct}
          productDeselect={productDeselect}
        />
      </div>

      {/* ---------- MODALS ---------- */}
      <SurpriseModal
        isOpen={isModalOpen}
        socket={socket}
        currentChat={currentChat._id}
        onRequestClose={() => setIsModalOpen(false)}
        selectedProduct={selectedProduct}
        productDeselect={productDeselect}
        user={currentChat.username}
      />

      {showDeleteConfirm && (
        <div
          className="overlay"
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "rgba(0,0,0,0.5)",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 1000,
          }}
        >
          <div
            className="confirm"
            style={{
              backgroundColor: "white",
              padding: "20px",
              borderRadius: "10px",
              maxWidth: "400px",
              textAlign: "center",
            }}
          >
            <p>Delete all messages?</p>
            <button
              style={{
                padding: "8px 16px",
                backgroundColor: "#f5f5f5",
                border: "1px solid #ddd",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={confirmDelete}
            >
              Yes
            </button>
            <button
              style={{
                padding: "8px 16px",
                backgroundColor: "#ff4444",
                color: "white",
                border: "none",
                borderRadius: "4px",
                cursor: "pointer",
              }}
              onClick={() => setShowDeleteConfirm(false)}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </Container>
  );
}

/* ================= STYLES ================= */

const Container = styled.div`
  height: 100vh;
  display: grid;
  grid-template-rows: 90vh 10vh;
  overflow: hidden;

  .top {
    display: grid;
    grid-template-columns: 50% 50%;
    height: 100%;
  }

  .left {
    display: flex;
    flex-direction: column;
    border-right: 1px solid #e5e7eb;
    overflow: hidden;
  }

  .right {
    overflow-y: auto;
    background: white;
    width: 100%;
    display: flex;
  }

  .header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.75rem 1rem;
    border-bottom: 1px solid #ddd;
  }

  .user {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    cursor: pointer;
  }

  .user img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
  }

  .actions {
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    padding: 0.5rem 1rem;
  }

  .messages {
    flex: 1;
    overflow-y: auto;
    padding: 1rem;
  }

  .bubble {
    position: relative;
    max-width: 70%;
    padding: 0.75rem;
    border-radius: 1rem;
    margin-bottom: 0.75rem;
    font-size: 0.9rem;
  }

  .sent {
    margin-left: auto;
    background: #dcf8c6;
  }

  .received {
    background: #f1f0f0;
  }

  .date-badge {
    margin: 1rem auto;
    padding: 0.3rem 0.8rem;
    background: #eaeaea;
    border-radius: 12px;
    font-size: 12px;
    width: fit-content;
  }

  .input {
    width: 100%;
    border-top: 1px solid #ddd;
    background: #080420;
    align-items: center;
  }
`;
